import { fetchClinicStudiesSupport } from '@/entities/study';
import { handleSubmitSupportRequest } from '@/entities/support';
import { INVALID_EMAIL_ERROR_MESSAGE, LOADING_TYPES } from '@/shared/config';
import { useSetState } from '@/shared/lib/customHooks';
import { isNumberByRegex, showMessageError, showMessageSuccess, validateEmail } from '@/shared/lib';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import { formattedStudyInfo } from '../handler';
import type { ChangeStudyTypeProps, ChangeStudyTypeState } from './changeStudyType.types';

const searchTypeList = [
  {
    label: t`Patient name`,
    value: 'Patient name',
    title: 'by Patient name',
  },
  {
    label: t`Study ID`,
    value: 'Study ID',
    title: 'by Study ID',
  },
];

/**
 * Holds the form state of the change-study-type support request: study search, selected studies
 * with their new study type, reply emails and message. Keeps the study search results in local
 * state, clears the search when the search type changes and the whole form when the facility
 * changes, and submits old and new study types per study through handleSubmitSupportRequest.
 *
 * @param props - Form props: category, selectedFacility, setLoadingPage and backToPrevious.
 * @returns An object carrying the search type list, the search results and their setter, the form
 *   state, and the remove, send, change, email, selection, search and pick handlers.
 */
export const useChangeStudyType = ({
  category,
  selectedFacility,
  setLoadingPage,
  backToPrevious,
}: ChangeStudyTypeProps) => {
  const loadOptionsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [dataSearch, setDataSearch] = useState<any[]>([]);
  const [state, setState] = useSetState<ChangeStudyTypeState>({
    emailList: [],
    emailInputValue: '',
    message: '',
    searchType: searchTypeList[0].value,
    errorObject: {},
    isSaving: false,
    inputValue: '',
    selectedArray: [],
    isOpenPopover: false,
    defaultOptionStudyId: [],
    isSearching: false,
  });

  /**
   * Searches the clinic studies of the selected facility for the change-study-type request. Sends the term as studyFid when the search type is 'Study ID' and as patientName when it is 'Patient name', includes RMA studies sorted descending and excludes support tickets, maps each result through formattedStudyInfo, and reports a load failure with an empty list on error.
   *
   * @param limit - Maximum number of studies to fetch.
   * @param searchText - Search term, empty by default.
   * @param type - Which field the term searches, 'Study ID' or 'Patient name'.
   * @returns A promise for the formatted study search entries, empty when the query failed.
   */
  const fetchStudiesInfo = async (limit: any, searchText = '', type = '') => {
    try {
      const queryFilter = {
        studyFid: type === 'Study ID' ? Number(searchText) : undefined,
        patientName: type === 'Patient name' ? searchText : undefined,
        includeRma: true,
        sortOrder: 'desc',
        facilityId: selectedFacility?.value,
        isSupportTicket: false,
        viewAll: true,
      };
      const studyInfo = await fetchClinicStudiesSupport(queryFilter, limit);
      /**
       * The fetched studies converted into the shape used by the study search options. Each item passes through formattedStudyInfo with lodash map.
       *
       * @returns The formatted study search entries.
       */
      const clonedArray = _.map(studyInfo, (item) => formattedStudyInfo(item));
      return clonedArray;
    } catch (_error) {
      showMessageError({ content: t`Failed to load studies info` });
      return [];
    }
  };

  /**
   * Debounces the study lookup behind the change-study-type search select by 300 ms. Answers with an empty list for a blank term or a non-numeric term under 'Study ID' search, otherwise resets the pending timer, raises isSearching, fetches ten studies, removes the ones already in selectedArray by value.studyId, and hands the remainder to both state and the callback, falling back to an empty list on error.
   *
   * @param inputValue - Term typed into the study search select.
   * @param callback - Consumer receiving the resolved study options.
   * @returns A promise settling once the options were handed to the callback.
   */
  const loadOptionsStudies = async (inputValue: any, callback: any) => {
    if (
      !inputValue ||
      (state.searchType === 'Study ID' && inputValue !== '' && !isNumberByRegex(inputValue))
    ) {
      callback([]);
    } else {
      if (loadOptionsTimeout.current) {
        clearTimeout(loadOptionsTimeout.current);
        loadOptionsTimeout.current = null;
      }

      loadOptionsTimeout.current = setTimeout(async () => {
        try {
          setState({ isSearching: true });
          if (inputValue !== '') {
            const dataLoaded = await fetchStudiesInfo(10, inputValue, state.searchType);
            const result = _.differenceBy(dataLoaded, state.selectedArray, 'value.studyId');
            setState({ defaultOptionStudyId: result, isSearching: false });
            callback(result);
          }
        } catch (error) {
          setState({ isSearching: false });
          console.error('Failed to search study: ', error);
          callback([]);
        }
      }, 300);
    }
  };

  /**
   * Drops one study from the change-study-type request. Deep-clones selectedArray, splices the entry at the given index out of it and stores the shortened list.
   *
   * @param index - Position of the study to remove inside selectedArray.
   * @returns Nothing.
   */
  const handleRemoveClick = (index: any) => {
    const tempArr = _.cloneDeep(state.selectedArray);
    tempArr.splice(index, 1);
    setState({ selectedArray: tempArr });
  };

  /**
   * Submits a change-study-type support request covering every study picked in the form. Requires a message, at least one selected study and a new study type on each of them, plus a non-empty and valid reply-to email list, then posts category, joined reply emails, facility id, trimmed message and one content entry per study carrying its old and new study type, confirming with a success toast and navigating back or reporting a send failure.
   *
   * @returns A promise settling once the request was sent and the page navigated back, or the errors stored.
   */
  const onClickSendMessage = async () => {
    setState({ isSaving: true });

    const errorObject: Record<string, any> = {};

    if (!state.message) {
      _.assign(errorObject, { message: t`Invalid value` });
    }

    if (!state.selectedArray.length) {
      _.assign(errorObject, { searchStudy: t`Invalid value` });
    }

    _.forEach(state.selectedArray, (item, index) => {
      if (item.value?.newStudyType === null) {
        if (!errorObject.selectedArray) {
          errorObject.selectedArray = [];
        }
        errorObject.selectedArray[index] = t`Invalid value`;
      }
    });

    const isValidEmailList = !_.isEmpty(state.emailList);
    if (!isValidEmailList) {
      _.assign(errorObject, { invalidEmails: t`Invalid value` });
    }

    const isValidCurrentInput = !state.emailInputValue || validateEmail(state.emailInputValue);
    if (!isValidCurrentInput) {
      _.assign(errorObject, { invalidEmails: INVALID_EMAIL_ERROR_MESSAGE });
    }

    if (!_.isEmpty(errorObject)) {
      setState({
        errorObject,
        isSaving: false,
      });
    } else {
      try {
        setLoadingPage(LOADING_TYPES.SUBMITTING);
        await handleSubmitSupportRequest({
          category,
          replyEmail: _.join(state.emailList, ','),
          facilityId: selectedFacility?.value as string,
          message: state.message.trim(),
          appName: 'Bioflux',
          contents: _.map(state.selectedArray, (item) => ({
            study: item.value?.studyId,
            studyType: {
              old: item.value?.studyType?.value,
              new: item.value?.newStudyType?.value,
            },
          })),
        });
        showMessageSuccess({
          content: t`We have received your request and will email you back when it has been completed`,
        });
        setLoadingPage(false);
        backToPrevious();
      } catch (error) {
        console.error('Failed to send support email: ', error);
        showMessageError({ content: t`Failed to send your message` });
      } finally {
        setState({ isSaving: false });
      }
    }
  };

  /**
   * Records one field of the change-study-type request and drops its error. Deep-clones errorObject without the entry keyed by the field name, additionally closes the popover when the search type changed, and commits the value together with the pruned error map.
   *
   * @param name - Key of the changed field, treated specially for 'searchType'.
   * @param value - Value entered for that field.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject[name];
    const setStateObject = {};
    if (name === 'searchType') {
      _.assign(setStateObject, { isOpenPopover: false });
    }
    _.assign(setStateObject, {
      [name]: value,
      errorObject: cloneErrorObject,
    });
    setState(setStateObject);
  };

  /**
   * Stores the reply-to email addresses of the change-study-type request using the previous state. Saves the new list, empties the pending email input and removes an existing invalidEmails error from a copy of errorObject.
   *
   * @param value - Accepted email addresses of the reply-to list.
   * @returns Nothing.
   */
  const onChangeEmailList = (value: any) => {
    setState((prev) => {
      const objectState = {
        emailList: value,
        emailInputValue: '',
      };
      const { errorObject } = prev;

      if (errorObject?.invalidEmails) {
        const cloneErrorObject = { ...errorObject };
        delete cloneErrorObject.invalidEmails;
        _.assign(objectState, { errorObject: cloneErrorObject });
      }
      return objectState;
    });
  };

  /**
   * Tracks the half-typed reply-to address of the change-study-type request from the previous state. Stores the input text and removes an existing invalidEmails error from a copy of errorObject.
   *
   * @param value - Current text of the email input.
   * @returns Nothing.
   */
  const onInputChange = (value: any) => {
    setState((prev) => {
      const objectState = {
        emailInputValue: value,
      };
      const { errorObject } = prev;

      if (errorObject?.invalidEmails) {
        const cloneErrorObject = { ...errorObject };
        delete cloneErrorObject.invalidEmails;
        _.assign(objectState, { errorObject: cloneErrorObject });
      }
      return objectState;
    });
  };

  /**
   * Sets the new study type chosen for one of the studies listed in the change-study-type request. Deep-clones selectedArray, assigns the value onto the value object of the entry at the given index, and deletes that index from the per-row error list.
   *
   * @param name - Key assigned on the study entry, such as the new study type.
   * @param value - Chosen value for that key.
   * @param index - Position of the study inside selectedArray.
   * @returns Nothing.
   */
  const onChangeSelection = (name: any, value: any, index: any) => {
    const clonedSelectedArray = _.cloneDeep(state.selectedArray);
    _.assign(clonedSelectedArray[index]?.value, { [name]: value });
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject.selectedArray?.[index];
    setState({
      selectedArray: clonedSelectedArray,
      errorObject: cloneErrorObject,
    });
  };

  /**
   * Accepts typing in the study search box of the change-study-type request. Strips every non-digit under 'Study ID' search and, when the cleaned term differs from the stored one, stores it and triggers loadOptionsStudies feeding setDataSearch.
   *
   * @param newValue - Raw text typed into the study search box.
   * @returns Nothing.
   */
  const handleSearch = (newValue: any) => {
    let value = newValue;
    if (state.searchType === 'Study ID') {
      value = _.replace(value, /[^0-9]/g, '');
    }
    if (value !== state.inputValue) {
      setState({ inputValue: value });
      loadOptionsStudies(value, setDataSearch);
    }
  };

  /**
   * Adds a study picked from the search dropdown to the change-study-type request. Appends the entry at the picked index of dataSearch to selectedArray, empties the search input, clears the searchStudy error and removes the chosen entry from the search results.
   *
   * @param newValue - Index of the picked entry inside the current search results.
   * @returns Nothing.
   */
  const handleChange = (newValue: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject.searchStudy;
    setState({
      selectedArray: [...state.selectedArray, dataSearch[newValue]],
      inputValue: '',
      errorObject: cloneErrorObject,
    });
    /**
     * The search results with the just-selected entry dropped. Built by lodash filter comparing each index against the picked index.
     *
     * @returns The remaining study search results.
     */
    const removeData = _.filter(dataSearch, (d, index) => index !== newValue);
    setDataSearch(removeData);
  };

  useEffect(() => {
    setState({
      inputValue: '',
      defaultOptionStudyId: [],
    });
  }, [state.searchType]);

  useEffect(() => {
    setState({
      emailList: [],
      emailInputValue: '',
      message: '',
      inputValue: '',
      selectedArray: [],
      defaultOptionStudyId: [],
      errorObject: {},
    });
  }, [selectedFacility]);

  return {
    searchTypeList,
    dataSearch,
    setDataSearch,
    state,
    handleRemoveClick,
    onClickSendMessage,
    onChange,
    onChangeEmailList,
    onInputChange,
    onChangeSelection,
    handleSearch,
    handleChange,
  };
};
